//! Import the necessary modules
import { API_URI } from "../../constants/consts";

//* Import Axios instance
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import types
import {
  type Pagination,
  type RegistrationResponse,
  type RegistrationDetails,
  type CreateRegistrationInput,
} from "./types/types";

/**
 * RegistrationAPI é um objeto utilitário de API para gerenciar operações CRUD
 * (criar, ler, atualizar, deletar) para registros através de requisições HTTP.
 */
export const registrationAPI = {
  /**
   * Busca uma lista paginada de registros da API.
   *
   * @param {string} search - Termo opcional para filtrar registros (padrão é uma string vazia).
   * @param {number} page - Número da página para paginação (padrão é 1).
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   * @param {number} perPage - Número de registros por página (padrão é 10).
   * @param {string} teamId - ID opcional do time para filtrar registros.
   * @param {string} unitId - ID opcional da unidade para filtrar registros.
   * @param {string} courseId - ID opcional do curso para filtrar registros.
   * @param {boolean} isActive - Filtro opcional para registros ativos/inativos.
   *
   * @returns {Promise<RegistrationResponse>} - Retorna uma resposta paginada contendo registros e informações de paginação.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10,
    teamId?: string,
    unitId?: string,
    courseId?: string,
    isActive?: boolean // Novo filtro
  ): Promise<RegistrationResponse> {
    const queryParams = new URLSearchParams();

    if (search) queryParams.append("search", search);
    if (teamId) queryParams.append("team_id", teamId);
    if (unitId) queryParams.append("unit_id", unitId);
    if (courseId) queryParams.append("course_id", courseId);
    if (typeof isActive !== "undefined")
      queryParams.append("is_active", isActive.toString()); 
    queryParams.append("page", page.toString());
    queryParams.append("per_page", perPage.toString());

    const url = `${API_URI}/admin/private/registrations?${queryParams.toString()}`;


    return api
      .request({
        url: url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;


        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
              currentUnitId: data.pagination.currentUnitId,
              currentCourseId: data.pagination.currentCourseId,
            }
          : {
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: 10,
              totalItems: 0,
              currentUnitId: undefined,
              currentCourseId: undefined,
            };

        return {
          registrations: data.registrations || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Erro ao buscar registros:", error);
        throw new Error("Falha ao buscar registros");
      });
  },

  /**
   * Busca um registro específico pelo seu ID na API.
   *
   * @param {string} id - UUID do registro a ser buscado.
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   *
   * @returns {Promise<RegistrationDetails>} - Retorna os dados do registro se encontrado.
   *
   * @throws {Error} - Lança um erro se o registro não for encontrado ou se a resposta for inválida.
   */
  getById: async function (
    id: string,
    token: string
  ): Promise<RegistrationDetails> {
    const url = `${API_URI}/admin/private/registrations/${id}`;

    return api
      .request({
        url: url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.result) {
          return response.data.data;
        } else {
          throw new Error("Registro não encontrado ou resposta inválida.");
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar registro:", error);
        throw new Error("Falha ao buscar registro");
      });
  },

  /**
   * Cria um novo registro enviando uma requisição POST para a API.
   *
   * @param {CreateRegistrationInput} data - Dados para o novo registro.
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   *
   * @returns {Promise<RegistrationDetails>} - Retorna os dados do registro criado.
   *
   * @throws {Error} - Lança um erro se a criação do registro falhar.
   */
  create: async function (
    data: CreateRegistrationInput,
    token: string
  ): Promise<RegistrationDetails> {
    const url = `${API_URI}/admin/private/registrations`;

    return api
      .request({
        url: url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          student_id: data.studentId,
          team_id: data.teamId,
          is_active: data.isActive,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Erro ao criar registro:", error);
        throw new Error("Falha ao criar registro");
      });
  },

  /**
   * Atualiza um registro existente enviando uma requisição PUT para a API.
   *
   * @param {string} id - UUID do registro a ser atualizado.
   * @param {Partial<CreateRegistrationInput>} data - Dados atualizados do registro.
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   *
   * @returns {Promise<RegistrationDetails>} - Retorna os dados do registro atualizado.
   *
   * @throws {Error} - Lança um erro se a atualização do registro falhar.
   */
  update: async function (
    id: string,
    data: Partial<CreateRegistrationInput>,
    token: string
  ): Promise<RegistrationDetails> {
    const updateData: Record<string, any> = {};
    if (data.studentId !== undefined) updateData.student_id = data.studentId;
    if (data.teamId !== undefined) updateData.team_id = data.teamId;
    if (data.isActive !== undefined) updateData.is_active = data.isActive;

    const url = `${API_URI}/admin/private/registrations/${id}`;

    return api
      .request({
        url: url,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: updateData,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Erro ao atualizar registro:", error);
        throw new Error("Falha ao atualizar registro");
      });
  },

  /**
   * Deleta um registro existente enviando uma requisição DELETE para a API.
   *
   * @param {string} id - UUID do registro a ser deletado.
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   *
   * @returns {Promise<void>} - Retorna o resultado da deleção.
   *
   * @throws {Error} - Lança um erro se a deleção falhar.
   */
  delete: async function (id: string, token: string): Promise<void> {
    const url = `${API_URI}/admin/private/registrations/${id}`;

    return api
      .request({
        url: url,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => undefined)
      .catch((error) => {
        console.error("Erro ao deletar registro:", error);
        throw new Error("Falha ao deletar registro");
      });
  },

  /**
   * Confirma uma matrícula enviando uma requisição PATCH para a API.
   *
   * @param {string} id - UUID da matrícula a ser confirmada.
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   *
   * @returns {Promise<RegistrationDetails>} - Retorna os dados da matrícula confirmada.
   *
   * @throws {Error} - Lança um erro se a confirmação falhar.
   */
  confirm: async function (
    id: string,
    token: string
  ): Promise<RegistrationDetails> {
    const url = `${API_URI}/admin/private/registrations/confirm/${id}`;

    return api
      .request({
        url: url,
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Erro ao confirmar matrícula:", error);
        throw new Error("Falha ao confirmar matrícula");
      });
  },

  /**
   * Confirma múltiplas matrículas de forma assíncrona enviando uma requisição PATCH para a API.
   *
   * @param {string[]} ids - Array de IDs das matrículas a serem confirmadas.
   * @param {string} token - Token de autenticação para ser enviado nos headers da requisição.
   *
   * @returns {Promise<{ success: boolean; message: string }>} - Retorna uma mensagem indicando que o processamento foi iniciado.
   *
   * @throws {Error} - Lança um erro se a confirmação assíncrona falhar.
   */
  confirmMultipleAsync: async function (
    ids: string[],
    token: string
  ): Promise<{ success: boolean; message: string }> {
    const url = `${API_URI}/admin/private/registrations/confirm-async`;


    return api
      .request({
        url: url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ids: ids,
        },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error(
          "Erro ao confirmar múltiplas matrículas assíncronas:",
          error
        );
        throw new Error("Falha ao confirmar múltiplas matrículas assíncronas");
      });
  },
};
